
import { Button, Card, Space, Table, Typography, Segmented, Badge, Input, DatePicker, Select, Col, Row, Form } from 'antd';
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProjectService } from '../../Services/Manage/ProjectService';
import { EditOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

dayjs.extend(isBetween);

const { RangePicker } = DatePicker;

const ProjectsPage = () => {
  const navigate = useNavigate();
  const [allProject, setAllProject] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('Live');
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);

  const getAllProjects = async () => {
    try {
      const request = await ProjectService.getAllProject();
      setAllProject(request.data);
      setFilteredProjects(request.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getAllProjects();
  }, []);

  const preStatusFiltered = useMemo(() => {
    let filtered = allProject;

    if (searchText) {
      filtered = filtered.filter(project =>
        Object.values(project).some(value =>
          value?.toString().toLowerCase().includes(searchText.toLowerCase())
        )
      );
    }

    if (dateRange && dateRange.length === 2) {
      const [start, end] = dateRange;
      filtered = filtered.filter(project => {
        const projectDate = dayjs(project.pro_start_date);
        return projectDate.isBetween(start, end, 'day', '[]');
      });
    }

    if (selectedDepartments.length > 0) {
      filtered = filtered.filter(project =>
        selectedDepartments.includes(project.department_name)
      );
    }

    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(project =>
        selectedStatuses.includes(project.pro_status)
      );
    }

    return filtered;
  }, [allProject, searchText, dateRange, selectedDepartments, selectedStatuses]);

  const statusCounts = useMemo(() => {
    const counts = {
      Live: 0,
      Hold: 0,
      Pending: 0,
      Archive: 0,
      Completed: 0,
    };

    preStatusFiltered.forEach((project) => {
      if (counts.hasOwnProperty(project.pro_status)) {
        counts[project.pro_status]++;
      }
    });

    return counts;
  }, [preStatusFiltered]);

  const departmentOptions = [...new Set(allProject.map(project => project.department_name))]
    .map(dept => ({ label: dept, value: dept }));

  const statusOptions = Object.keys(statusCounts).map(status => ({
    label: status,
    value: status
  }));
  useEffect(() => {
    let filtered = allProject;


    if (searchText) {
      filtered = filtered.filter(project =>
        Object.values(project).some(value =>
          value?.toString().toLowerCase().includes(searchText.toLowerCase())
        )
      );
    }

    if (dateRange && dateRange.length === 2) {
      const [start, end] = dateRange;
      filtered = filtered.filter(project => {
        const projectDate = dayjs(project.pro_start_date);
        return projectDate.isBetween(start, end, 'day', '[]');
      });
    }

    if (selectedDepartments.length > 0) {
      filtered = filtered.filter(project =>
        selectedDepartments.includes(project.department_name)
      );
    }

    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(project =>
        selectedStatuses.includes(project.pro_status)
      );
    }

    if (selectedStatus && selectedStatus !== "All") {
      filtered = filtered.filter(project => project.pro_status === selectedStatus);
    }

    setFilteredProjects(filtered);
  }, [
    allProject,
    searchText,
    dateRange,
    selectedDepartments,
    selectedStatuses,
    selectedStatus
  ]);

  const columns = [
    {
      title: 'SI No',
      dataIndex: 'key',
      width: '7%',
      align: 'center',
      key: 'key',
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Quote No',
      dataIndex: 'qu_no',
      key: 'qu_no',
    },
    {
      title: 'Project No',
      dataIndex: 'pro_no',
      key: 'pro_no',
    },
    {
      title: 'Project Name',
      dataIndex: 'pro_name',
      key: 'pro_name',
    },
    {
      title: 'Department',
      dataIndex: 'department_name',
      key: 'department_name',
    },
    {
      title: 'Team Size',
      dataIndex: 'pro_engineer',
      key: 'pro_engineer',
      render: (value) => {
        try {
          const arr = typeof value === 'string' ? JSON.parse(value) : value;
          return Array.isArray(arr) ? arr.length : 0;
        } catch (e) {
          return 0;
        }
      },
    },
    {
      title: 'Start Date',
      dataIndex: 'pro_start_date',
      key: 'pro_start_date',
      render: (text) => new Date(text).toLocaleDateString(),
    },
    {
      title: 'Target End Date',
      dataIndex: 'pro_target_end_date',
      key: 'pro_target_end_date',
      render: (text) => new Date(text).toLocaleDateString(),
    },
    {
      title: 'Status',
      dataIndex: 'pro_status',
      key: 'pro_status',
    },
    {
      title: 'Added On',
      dataIndex: 'added_on',
      key: 'added_on',
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: 'Action',
      dataIndex: 'Action',
      key: 'Action',
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            onClick={() => navigate('/projects/view', { state: { id: record.id, action: 'View', enableEdit: false } })}
            icon={<EyeOutlined />}
            size="large"
          >
            View
          </Button>
          <Button
            onClick={() => navigate('/projects/edit', { state: { id: record.id, action: 'Edit', enableEdit: true } })}
            icon={<EditOutlined />}
            size="large"
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  const styles = {
    badge: {
      backgroundColor: "#1b1660ff",
      fontSize: 14,
      minWidth: 24,
      height: 24,
      lineHeight: "24px",
      marginLeft: 10
    },
    filterContainer: {
      marginBottom: 16,
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      maxWidth: 400
    }
  };

  const segmentOptions = [
    {
      label: (
        <span>
          All <Badge count={preStatusFiltered.length || 0} style={styles.badge} />
        </span>
      ),
      value: "All"
    },
    {
      label: (
        <span>
          Live <Badge count={statusCounts["Live"] || 0} style={styles.badge} />
        </span>
      ),
      value: "Live"
    },
    {
      label: (
        <span>
          Hold <Badge count={statusCounts["Hold"] || 0} style={styles.badge} />
        </span>
      ),
      value: "Hold"
    },
    {
      label: (
        <span>
          Pending <Badge count={statusCounts["Pending"] || 0} style={styles.badge} />
        </span>
      ),
      value: "Pending"
    },
    {
      label: (
        <span>
          Archive <Badge count={statusCounts["Archive"] || 0} style={styles.badge} />
        </span>
      ),
      value: "Archive"
    },
    {
      label: (
        <span>
          Completed <Badge count={statusCounts["Completed"] || 0} style={styles.badge} />
        </span>
      ),
      value: "Completed"
    }
  ];

  return (
    <Card
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          Manage Projects
        </Typography.Title>
      }
      extra={
        <Space>

          <Button
          size="large"
          type="primary"
          onClick={() => navigate('/project-expenses', { state: { action: 'Edit', enableEdit: true } })}
        >
          Create Project Expenses
        </Button>

        <Button
          size="large"
          type="primary"
          onClick={() => navigate('/projects/create', { state: { action: 'Edit', enableEdit: true } })}
        >
          Add Project
        </Button>

        </Space>
      }
    >

   

      {/* <Col xs={24} sm={12} md={6}>
            <Form.Item layout='vertical' label="Select Status">
              <Select
                allowClear
                mode="multiple"
                placeholder="Select statuses"
                options={statusOptions}
                value={selectedStatuses}
                onChange={setSelectedStatuses}
                size="large"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col> */}


      <div style={{ paddingBottom: 20 }} />
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
        <Segmented
          size='large'
          options={segmentOptions}
          value={selectedStatus}
          onChange={(value) => setSelectedStatus(value)}
          style={{ marginBottom: 16 }}
        />

      </div>

         <Card>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Search" layout="vertical">
              <Input
                allowClear
                placeholder="Search..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                size="large"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Select Date" layout="vertical">
              <RangePicker
                allowClear
                size="large"
                onChange={(dates) => setDateRange(dates)}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Select Departments" layout="vertical">
              <Select
                allowClear
                mode="multiple"
                placeholder="Select departments"
                options={departmentOptions}
                value={selectedDepartments}
                onChange={setSelectedDepartments}
                size="large"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>


      <Table
        scroll={{ x: 'max-content' }}
        dataSource={filteredProjects
          .slice()
          .sort((a, b) => new Date(b.updated_on) - new Date(a.updated_on))
        }
        columns={columns}
      />

    </Card>
  );
};

export default ProjectsPage;
